import { createRouter, createWebHistory } from 'vue-router'
import {
  AGENDA_PILOT_SURFACE_ROLES,
  AGENDA_ALL_ROLES,
  AGENDA_COORDINATION_ROLES,
  canonicalGuard,
  getRedirectForRole
} from './guards'
import type { RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useNavigationStore } from '@/stores/navigation'
import { isPublicDevSurfaceEnabled } from '@/config/runtimeConfig'

// � Dashboard Routes Import
import dashboardRoutes from './routes/dashboard'

// 🔧 Use lazy-load para vistas pesadas y evitar cargar de inicio
const GroupsManagementSimple = () => import('@/views/SuperAdmin/GroupsManagementSimple.vue')
const TestBasic = () => import('@/views/management/TestBasic.vue')
const CompositionTest = () => import('@/views/management/CompositionTest.vue')
const AgencyManagementComposition = () => import('@/views/management/AgencyManagementComposition.vue')

const PUBLIC_DEV_SURFACES_ENABLED = isPublicDevSurfaceEnabled()
const LAB_SURFACE_FALLBACK = '/not-found'

function createContainedLabRoute(
  route: RouteRecordRaw,
  fallbackPath: string = LAB_SURFACE_FALLBACK
): RouteRecordRaw {
  if (PUBLIC_DEV_SURFACES_ENABLED) {
    return route
  }

  return {
    path: route.path,
    name: route.name,
    redirect: () => {
      if (fallbackPath !== LAB_SURFACE_FALLBACK) {
        return fallbackPath
      }

      const authStore = useAuthStore()
      return authStore.isAuthenticated ? '/dashboard' : LAB_SURFACE_FALLBACK
    },
    meta: {
      ...(route.meta || {}),
      containedFromPreprod: true,
    },
  }
}

function resolveCanonicalAliasRedirect(
  allowedRoles: string[],
  defaultPath: string
): string {
  const authStore = useAuthStore()
  const currentRole = authStore.userRole || ''

  if (allowedRoles.includes(currentRole)) {
    return getRedirectForRole(currentRole)
  }

  return defaultPath
}

function createLegacyAuthenticatedRedirect(
  path: string,
  name: string,
  redirect: RouteRecordRaw['redirect'],
  roles: string[]
): RouteRecordRaw {
  return {
    path,
    name,
    redirect,
    meta: {
      requiresAuth: true,
      roles,
      title: 'Redireccion legacy al dashboard canonico',
      group: 'legacy-redirects',
      legacyRedirect: true,
    },
  }
}

// 🧭 Enhanced Route Configuration with Advanced Guards
const routes: Array<RouteRecordRaw> = [
  {
    path: '/',
    name: 'Home',
    component: () => import('@/views/WelcomePage.vue'),
    meta: {
      requiresAuth: false,
      // P13-V7B-MIN C2 — authenticated convergence at the application root.
      // An authenticated actor landing on `/` used to stay on the public welcome surface, so the
      // root was a second answer to "where does this session belong". `guestOnly` routes it through
      // the SAME canonical authority the guard already applies (canonicalGuard → getRedirectForRole),
      // so no new redirect logic is introduced. Guests still see the welcome page unchanged.
      guestOnly: true,
      title: 'MGI',
      layout: 'default'
    }
  },
  createContainedLabRoute({
    path: '/kpi-chart-demo',
    name: 'KPIChartDemo',
    component: () => import('@/views/KPIChartDemo.vue'),
    meta: { 
      requiresAuth: false,
      title: '📊 KPI Chart Demo - Phase 2.3',
      layout: 'minimal'
    }
  }),
  // 🧪 PASO 5.12.1: Test del Dashboard Consolidado
  createContainedLabRoute({
    path: '/test-consolidated-dashboard',
    name: 'TestConsolidatedDashboard',
    component: () => import('@/views/test/ConsolidatedDashboardTest.vue'),
    meta: { 
      requiresAuth: false,
      title: '🧪 Test Dashboard Consolidado',
      layout: 'minimal'
    }
  }),
  createContainedLabRoute({
    path: '/role-selector',
    name: 'RoleSelector',
    component: () => import('@/components/RoleSelector.vue'),
    meta: { 
      requiresAuth: false,
      title: 'Selector de Roles MGI',
      layout: 'minimal'
    }
  }),

  // � CENTRO DE REPORTES EMPRESARIAL INDEPENDIENTE
  {
    path: '/reports/organizational',
    name: 'OrganizationalReportsIndependent',
    component: () => import('@/views/reports/OrganizationalReportsCenter.vue'),
    meta: {
      requiresAuth: true,
      title: 'Centro de Reportes Empresarial',
      roles: ['super_admin', 'group_manager'],
      layout: 'minimal', // Layout independiente sin menú lateral
      breadcrumb: [
        { name: 'Reportes Empresariales' }
      ],
      analytics: {
        category: 'Reports',
        action: 'view_independent',
        label: 'organizational-reports-standalone'
      }
    }
  },

  // �🛠️ Dev Center Routes
  createContainedLabRoute({
    path: '/dev-center',
    name: 'DevCenter',
    component: () => import('@/views/DevCenterView.vue'),
    meta: { 
      requiresAuth: false,
      title: '🛠️ MGI Development Center',
      layout: 'minimal'
    }
  }),
  createContainedLabRoute({
    path: '/dev-center/components',
    name: 'ComponentsShowcase',
    component: () => import('@/views/ComponentsShowcaseView.vue'),
    meta: { 
      requiresAuth: false,
      title: '🧩 Components Showcase',
      layout: 'minimal'
    }
  }),
  createContainedLabRoute({
    path: '/dev-center/components/filter-control',
    name: 'FilterControlDemo',
    component: () => import('@/views/FilterControlDemoView.vue'),
    meta: { 
      requiresAuth: false,
      title: '🔍 FilterControl Demo',
      layout: 'minimal'
    }
  }),
  createContainedLabRoute({
    path: '/dev-center/forms/optimized',
    name: 'OptimizedFormsDemo',
    component: () => import('@/views/OptimizedFormsDemo.vue'),
    meta: { 
      requiresAuth: false,
      title: '📝 Formularios Optimizados Demo',
      layout: 'minimal'
    }
  }),
  createContainedLabRoute({
    path: '/dev-center/api-testing',
    name: 'ApiTesting',
    component: () => import('@/views/dev-center/ApiTestingView.vue'),
    meta: { 
      requiresAuth: false,
      title: '🔧 API Testing Center',
      layout: 'minimal'
    }
  }),
  createContainedLabRoute({
    path: '/dev-center/performance-metrics',
    name: 'PerformanceMetrics',
    component: () => import('@/views/dev-center/PerformanceMetricsView.vue'),
    meta: { 
      requiresAuth: false,
      title: '📊 Performance Metrics',
      layout: 'minimal'
    }
  }),
  createContainedLabRoute({
    path: '/dev-center/fe-metrics',
    name: 'FrontendMetrics',
    component: () => import('@/views/dev-center/FrontendMetricsView.vue'),
    meta: {
      requiresAuth: false,
      title: '⏱️ Frontend Metrics (feMetrics)',
      layout: 'minimal'
    }
  }),
  createContainedLabRoute({
    path: '/dev-center/roles',
    name: 'RolesManagement',
    component: () => import('@/views/dev-center/RolesManagementView.vue'),
    meta: { 
      requiresAuth: false,
      title: '👥 Roles Management',
      layout: 'minimal'
    }
  }),
  createContainedLabRoute({
    path: '/dev-center/dashboard-test',
    name: 'DashboardTest',
    component: () => import('@/views/dev/DashboardTest.vue'),
    meta: { 
      requiresAuth: false,
      title: '🧪 Dashboard Integration Test',
      layout: 'minimal'
    }
  }),
  createContainedLabRoute({
    path: '/dev-center/migration',
    name: 'MigrationTools',
    component: () => import('@/views/dev-center/MigrationToolsView.vue'),
    meta: { 
      requiresAuth: false,
      title: '🔄 Migration Tools',
      layout: 'minimal'
    }
  }),
  createContainedLabRoute({
    path: '/dev-center/docs',
    name: 'Documentation',
    component: () => import('@/views/dev-center/DocumentationView.vue'),
    meta: { 
      requiresAuth: false,
      title: '📚 Documentation',
      layout: 'minimal'
    }
  }),
  createContainedLabRoute({
    path: '/dev-center/analytics',
    name: 'DevAnalytics',
    component: () => import('@/views/dev-center/DevAnalyticsView.vue'),
    meta: { 
      requiresAuth: false,
      title: '📊 Dev Analytics',
      layout: 'minimal'
    }
  }),
  createContainedLabRoute({
    path: '/dev-center/rbac-matrix',
    name: 'RbacMatrix',
    component: () => import('@/views/dev-center/RbacMatrixView.vue'),
    meta: {
      requiresAuth: false,
      title: '🔐 RBAC Matrix',
      layout: 'minimal'
    }
  }),
  createContainedLabRoute({
    path: '/dev-center/components/data-table',
    name: 'DataTableDemo',
    component: () => import('@/views/dev-center/components/DataTableDemoView.vue'),
    meta: { 
      requiresAuth: false,
      title: '📊 DataTable Demo',
      layout: 'minimal'
    }
  }),
  createContainedLabRoute({
    path: '/dev-center/components/sidebar-nav',
    name: 'SidebarNavDemo',
    component: () => import('@/views/dev-center/components/SidebarNavDemoView.vue'),
    meta: { 
      requiresAuth: false,
      title: '🧭 SidebarNav Demo',
      layout: 'minimal'
    }
  }),
  createContainedLabRoute({
    path: '/dev-center/components/user-profile',
    name: 'UserProfileDemo',
    component: () => import('@/views/dev-center/components/UserProfileDemoView.vue'),
    meta: { 
      requiresAuth: false,
      title: '👤 UserProfile Demo',
      layout: 'minimal'
    }
  }),
  createContainedLabRoute({
    path: '/dev-center/components/notifications',
    name: 'NotificationsDemo',
    component: () => import('@/views/dev-center/components/NotificationsDemoView.vue'),
    meta: { 
      requiresAuth: false,
      title: '🔔 Notifications Demo',
      layout: 'minimal'
    }
  }),
  
  // 🔐 Authentication Routes
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/views/auth/RealLogin.vue'),
    meta: { 
      requiresAuth: false, 
      guestOnly: true,
      title: 'Iniciar Sesión',
      layout: 'auth'
    }
  },
  // Ruta directa para login real (siempre disponible)
  {
    path: '/auth/real-login',
    name: 'RealLoginDirect',
    component: () => import('@/views/auth/RealLogin.vue'),
    meta: {
      requiresAuth: false,
      guestOnly: true,
      title: 'Iniciar Sesión (Real)',
      layout: 'auth'
    }
  },
  createContainedLabRoute({
    path: '/login-simple',
    name: 'LoginSimple',
    component: () => import('@/views/auth/SimpleAuth.vue'),
    meta: { 
      requiresAuth: false, 
      guestOnly: true,
      title: 'Login Simple',
      layout: 'auth'
    }
  }, '/auth/real-login'),

  // 🏠 Dashboard Routes - Role-based with Advanced Guards
  {
    path: '/dashboard',
    name: 'Dashboard',
    component: () => import('@/views/RoleBasedRedirect.vue'),
    meta: { 
      requiresAuth: true,
      title: 'Dashboard',
      breadcrumb: 'Dashboard',
      icon: 'mdi-view-dashboard',
      group: 'main'
    }
  },

  // 🎯 Enterprise Role-Specific Dashboard Routes (7 roles completos)
  {
    path: '/super-admin',
    name: 'SuperAdminDashboard',
    component: () => import('@/views/dashboards/SuperAdminDashboard.vue'),
    meta: { 
      requiresAuth: true,
      roles: ['super_admin'],
      title: 'Super Admin Dashboard',
      breadcrumb: 'Super Admin',
      icon: 'mdi-shield-crown',
      group: 'dashboard'
    }
  },
  {
    path: '/advanced-services',
    name: 'AdvancedServices',
    component: () => import('@/components/advanced/AdvancedServicesDashboard.vue'),
    meta: { 
      requiresAuth: true,
  roles: ['super_admin'],
      title: 'Servicios Avanzados MGI',
      breadcrumb: 'Servicios Avanzados',
      icon: 'mdi-rocket-launch',
      group: 'advanced'
    }
  },
  {
    path: '/super-admin/business-intelligence',
    name: 'SuperAdminBusinessIntelligence',
    component: () => import('@/views/dashboards/BusinessIntelligenceExecutive.vue'),
    meta: { 
      requiresAuth: true,
      roles: ['super_admin'],
      title: 'Business Intelligence Ejecutivo',
      breadcrumb: [{ name: 'BI Ejecutivo' }],
      icon: 'mdi-brain',
      group: 'dashboard'
    }
  },
  {
    path: '/super-admin/groups-management',
    name: 'SuperAdminGroupsManagement',
    component: GroupsManagementSimple, // ✅ VOLVIENDO AL QUE FUNCIONABA
    meta: { 
      requiresAuth: true,
  roles: ['super_admin'],
      title: 'MGI - Gestión de Grupos',
      breadcrumb: [{ name: 'Sistema' }, { name: 'Gestión de Grupos' }],
      icon: 'mdi-account-group',
      group: 'administration'
    }
  },
  {
    path: '/super-admin/agencies-management',
    name: 'SuperAdminAgenciesManagement',
    component: AgencyManagementComposition,
    meta: { 
      requiresAuth: true,
  roles: ['super_admin'],
      title: 'MGI - Gestión de Agencias (COMPOSITION API)',
      breadcrumb: [{ name: 'Sistema' }, { name: 'Gestión de Agencias' }],
      icon: 'mdi-store-multiple',
      group: 'administration'
    }
  },
  // 👥 Gestión de Asignaciones de Empleados
  {
    path: '/super-admin/employee-assignments',
    name: 'EmployeeAssignments',
    component: () => import('@/views/SuperAdmin/EmployeeAssignments.vue'),
    meta: { 
      requiresAuth: true,
      roles: ['super_admin', 'group_manager'],
      title: 'Gestión de Asignaciones de Empleados',
      breadcrumb: [{ name: 'Sistema' }, { name: 'Asignaciones de Empleados' }],
      icon: 'mdi-account-arrow-right',
      group: 'administration'
    }
  },
  // 📊 PASO 5.12.1: Vista Ejecutiva - SuperAdmin
  {
    path: '/super-admin/dashboard-consolidated',
    name: 'SuperAdminDashboardConsolidated',
    component: () => import('@/components/dashboard/GroupsAgenciesConsolidatedDashboard.vue'),
    meta: { 
      requiresAuth: true,
      roles: ['super_admin'],
      title: 'Vista Ejecutiva - SuperAdmin',
      breadcrumb: [{ name: 'Dashboard' }, { name: 'Vista Ejecutiva' }],
      icon: 'mdi-view-dashboard-variant',
      group: 'dashboard'
    }
  },
  // 💰 Reportes de Gastos - SuperAdmin (acceso global a todos los grupos)
  {
    path: '/super-admin/expense-reports',
    name: 'SuperAdminExpenseReports',
    component: () => import('@/views/super-admin/SuperAdminExpenseReportsView.vue'),
    meta: { 
      requiresAuth: true,
      roles: ['super_admin'],
      title: 'Reportes de Gastos - SuperAdmin',
      breadcrumb: [{ name: 'Dashboard' }, { name: 'Reportes de Gastos' }],
      icon: 'mdi-chart-box',
      group: 'reports'
    }
  },

  {
    path: '/group-manager',
    name: 'GroupManagerDashboard',
    component: () => import('@/views/dashboards/GroupManagerDashboard.production.vue'),
    meta: { 
      requiresAuth: true,
      roles: ['group_manager'],
      // P13-V7B (DEC-V7B-01): the home states the actor's PERSISTED group partition. The former
      // `isRegionalManager` / `regionalAccess` / `autoFilterRegions` flags named a region authority
      // that exists nowhere in the backend; nothing read them and they are gone.
      title: 'Mis grupos',
      breadcrumb: 'Gerente de Grupo',
      icon: 'mdi-account-group',
      group: 'dashboard'
    }
  },
  // P13-V1D (C10, decisión #19): la ruta /group-manager/incidents fue RETIRADA — era un dead-end
  // honesto ("No disponible": su servicio lanzaba 501 localmente) para un rol CONDICIONAL del
  // piloto, y la sidebar además la anunciaba a super_admin (garantizando /access-denied). El home
  // de group_manager conserva sus funciones no-incidencias.
  // P13-V2A C7 (retiro simultáneo con el backend 410): la ruta /group-manager/stock fue RETIRADA —
  // su vista era un dead-end honesto (el servicio regional lanzaba 501 localmente) y el backend
  // inventory-regional es un 410 estable desde C1. La vista se elimina en V2B.
  // P13-V7B: the route /group-manager/maps is RETIRED with its view. The page issued no request at
  // all and still published figures: four tiles and a chip printing 0 incidences / 0 agencies / 0
  // supervisors, an "En línea" status that meant only that the map widget had initialised, and the
  // sentence "No hay incidencias activas en tus regiones" over an array nobody filled. Its filter was
  // a hard-coded table of regions with invented administration and agency ranges. The producers a map
  // would need (points, routes) are vendor-only, and map/route capability belongs to V4; nothing is
  // built here in its place. The group manager's partition is stated on its home.
  // ── Lote 6.1: Administration Manager — identidad operativa real ──
  //
  // P13-V7A-ADMINISTRATION-MANAGER-CAPABILITY-CLOSURE-WS1 · this is the ONE landing of the role, and it
  // is now backed: it reads the actor's own administration (`GET /api/administrations/:id`, the
  // reference statistics and `GET /:id/metrics`) and offers the one write the server projects for the
  // role (`org.administration.update`, K3-W17, shown only when the session's `availableActions` lists
  // it). The two "Lote 6.2/6.3" records that used to sit here (`/administration-manager/assignments`,
  // `/administration-manager/substitutes`) are RETIRED: both mounted the vendor task-assignment screen
  // (`views/assignments/AssignmentsManagement.vue`, which never read the route, so "suplencias" was the
  // same page), its user pickers called `/api/users`, which refuses the role, its auto-assign posted to a
  // doubled `/api/api/...` path, nothing linked to them, and the backend write tier they fronted is now
  // vendor-only (`DEC-V7A-02`). super_admin keeps the same component at `/assignments/management`.
  {
    path: '/administration-manager',
    name: 'AdministrationManagerDashboard',
    component: () => import('@/views/Administrator/Dashboard.vue'),
    meta: {
      requiresAuth: true,
      roles: ['administration_manager'],
      title: 'Mi administración',
      breadcrumb: 'Administration Manager',
      icon: 'mdi-account-tie',
      group: 'dashboard'
    }
  },
  {
    path: '/field-supervisor',
    name: 'FieldSupervisorDashboard',
    component: () => import('@/views/dashboards/SupervisorZonaDashboard.vue'),
    meta: { 
      requiresAuth: true,
      roles: ['field_supervisor'],
      title: 'Dashboard Supervisor de Zona',
      breadcrumb: 'Supervisor de Zona',
      icon: 'mdi-account-supervisor',
      group: 'dashboard',
      layout: 'dashboard'
    }
  },
  {
    path: '/supervisor/incidents',
    name: 'SupervisorIncidents',
    component: () => import('@/views/incidents/SupervisorIncidentsView.vue'),
    meta: { 
      requiresAuth: true,
      roles: ['field_supervisor'],
      title: 'Gestión de Incidencias - Supervisor',
      breadcrumb: [
        { name: 'Field Supervisor', path: '/field-supervisor' },
        { name: 'Incidencias' }
      ],
      icon: 'mdi-clipboard-alert',
      group: 'operations'
    }
  },
  {
    path: '/supervisor/maps',
    name: 'SupervisorMaps',
    component: () => import('@/views/maps/SupervisorInteractiveMap.vue'),
    meta: { 
      requiresAuth: true,
      roles: ['super_admin'],
      // W2 · ROLE-HOME-API-FIELD-SUPERVISOR: `field_supervisor` removed. The backend role-home
      // perimeter contains this page's API family to vendor-only, so admitting the client here
      // would mount a business component whose every read answers 403. The route and its
      // component are PRESERVED for their future owner (§20) — only client admission is removed.
      title: 'Mapas Interactivos - Supervisor',
      breadcrumb: [
        { name: 'Field Supervisor', path: '/field-supervisor' },
        { name: 'Mapas' }
      ],
      icon: 'mdi-map-marker-radius',
      group: 'operations'
    }
  },
  // 🆕 Nuevas rutas para Inventario y Gastos Fijos del Field Supervisor
  {
    path: '/field-supervisor/inventory-request',
    name: 'FieldSupervisorInventoryRequest',
    component: () => import('@/views/field-supervisor/FieldInventoryRequestView.vue'),
    meta: { 
      requiresAuth: true,
      roles: ['super_admin'],
      // W2 · ROLE-HOME-API-FIELD-SUPERVISOR: `field_supervisor` removed. The backend role-home
      // perimeter contains this page's API family to vendor-only, so admitting the client here
      // would mount a business component whose every read answers 403. The route and its
      // component are PRESERVED for their future owner (§20) — only client admission is removed.
      title: 'Solicitar Inventario - Supervisor',
      breadcrumb: [
        { name: 'Field Supervisor', path: '/field-supervisor' },
        { name: 'Solicitar Inventario' }
      ],
      icon: 'mdi-package-variant-plus',
      group: 'operations'
    }
  },
  {
    path: '/field-supervisor/fixed-expenses',
    name: 'FieldSupervisorFixedExpenses',
    component: () => import('@/views/field-supervisor/FieldFixedExpensesView.vue'),
    meta: { 
      requiresAuth: true,
      roles: ['super_admin'],
      // W2 · ROLE-HOME-API-FIELD-SUPERVISOR: `field_supervisor` removed. The backend role-home
      // perimeter contains this page's API family to vendor-only, so admitting the client here
      // would mount a business component whose every read answers 403. The route and its
      // component are PRESERVED for their future owner (§20) — only client admission is removed.
      title: 'Gastos Fijos - Supervisor',
      breadcrumb: [
        { name: 'Field Supervisor', path: '/field-supervisor' },
        { name: 'Gastos Fijos' }
      ],
      icon: 'mdi-cash-multiple',
      group: 'operations'
    }
  },
  {
    path: '/field-supervisor/expense-reports',
    name: 'FieldSupervisorExpenseReports',
    component: () => import('@/views/field-supervisor/FieldExpenseReportsView.vue'),
    meta: { 
      requiresAuth: true,
      roles: ['super_admin'],
      // W2 · ROLE-HOME-API-FIELD-SUPERVISOR: `field_supervisor` removed. The backend role-home
      // perimeter contains this page's API family to vendor-only, so admitting the client here
      // would mount a business component whose every read answers 403. The route and its
      // component are PRESERVED for their future owner (§20) — only client admission is removed.
      // P13-V7B: `group_manager` removed for the SAME reason — the three reads of this page
      // (`/api/field-supervisor/groups`, `/expenses-categories`, `/expenses`) are vendor-only, so
      // the role was admitted to a page that could only answer 403.
      title: 'Reportes de Gastos - Supervisor',
      breadcrumb: [
        { name: 'Field Supervisor', path: '/field-supervisor' },
        { name: 'Reportes de Gastos' }
      ],
      icon: 'mdi-chart-box',
      group: 'reports'
    }
  },
  {
    path: '/route-operator',
    name: 'RouteOperatorDashboard',
    component: () => import('@/views/dashboards/OperadorRutaDashboard.vue'),
    meta: { 
      requiresAuth: true,
      roles: ['route_operator'],
      title: 'Dashboard Operador de Ruta',
      breadcrumb: 'Operador Ruta',
      icon: 'mdi-truck',
      group: 'dashboard'
    }
  },
  {
    path: '/route-operator/pos-management',
    name: 'RoutePOSManagement',
    component: () => import('@/views/pos/RoutePOSManagement.vue'),
    meta: { 
      requiresAuth: true,
      roles: ['route_operator'],
      title: 'Gestión de Puntos de Venta - Ruta',
      breadcrumb: [
        { name: 'Route Operator', path: '/route-operator' },
        { name: 'Gestión POS' }
      ],
      icon: 'mdi-store-marker',
      group: 'operations'
    }
  },
  {
    path: '/maps/route-operator',
    name: 'RouteOperatorMap',
    component: () => import('@/views/maps/RouteOperatorMap.vue'),
    meta: { 
      requiresAuth: true,
      roles: ['route_operator'],
      title: 'Mapa de Mi Ruta Específica',
      breadcrumb: [
        { name: 'Route Operator', path: '/route-operator' },
        { name: 'Mapa de Ruta' }
      ],
      icon: 'mdi-map-marker-path',
      group: 'maps',
      permissions: [
        'maps:view_own_route',
        'routes:view_details',
        'pos:view_on_route',
        'incidents:view_route_specific'
      ]
    }
  },
  {
    path: '/route-operator/incidents',
    name: 'RouteOperatorIncidents',
    component: () => import('@/views/route-operator/RouteIncidentsView.vue'),
    meta: { 
      requiresAuth: true,
      roles: ['route_operator'],
      title: 'Mis Incidencias',
      breadcrumb: [
        { name: 'Route Operator', path: '/route-operator' },
        { name: 'Mis Incidencias' }
      ],
      icon: 'mdi-alert-circle-outline',
      group: 'incidents',
      permissions: [
        'incidents:create',
        'incidents:view_own',
        'incidents:list_own'
      ]
    }
  },
  {
    path: '/agency-employee',
    name: 'AgencyEmployeeDashboard',
    component: () => import('@/views/dashboards/EmpleadoAgenciaDashboard.vue'),
    meta: { 
      requiresAuth: true,
      roles: ['agency_employee'],
      title: 'Dashboard Empleado Agencia',
      breadcrumb: 'Empleado Agencia',
      icon: 'mdi-office-building-marker',
      group: 'dashboard'
    }
  },
  {
    // Lote 6.4: Vista real "Mi turno" del agency_employee
    path: '/agency-employee/my-turn',
    name: 'AgencyEmployeeMyTurn',
    redirect: '/agency-employee',
    meta: {
      requiresAuth: true,
      roles: ['agency_employee'],
      title: 'Mi turno — Empleado Agencia',
      breadcrumb: [{ name: 'Empleado Agencia', path: '/agency-employee' }, { name: 'Mi turno' }],
      icon: 'mdi-clock-outline',
      group: 'operations'
    }
  },
  {
    // Lote 6.5: Panel QC de recepción para agency_employee (función que era de data_collector)
    path: '/agency-employee/qc',
    name: 'AgencyEmployeeQC',
    redirect: '/agency-employee',
    meta: {
      requiresAuth: true,
      roles: ['agency_employee'],
      title: 'Control de Calidad — Empleado Agencia',
      breadcrumb: [{ name: 'Empleado Agencia', path: '/agency-employee' }, { name: 'QC' }],
      icon: 'mdi-check-decagram',
      group: 'operations'
    }
  },
  {
    path: '/employee/incidents',
    name: 'EmployeeIncidentsTracker',
    component: () => import('@/views/mobile/EmployeeIncidentsTracker.vue'),
    meta: { 
      requiresAuth: true,
      roles: ['agency_employee'],
      title: 'Mis Incidencias',
      breadcrumb: [
        { name: 'Empleado Agencia', path: '/agency-employee' },
        { name: 'Mis Incidencias' }
      ],
      icon: 'mdi-clipboard-list-outline',
      group: 'operations',
      mobileOptimized: true
    }
  },
  {
    path: '/department-head',
    name: 'DepartmentHeadDashboard',
    component: () => import('@/views/dashboards/DepartmentHeadDashboard.production.vue'),
    meta: { 
      requiresAuth: true,
      roles: ['department_head'],
      title: 'Dashboard Department Head',
      breadcrumb: 'Department Head',
      icon: 'mdi-account-tie',
      group: 'dashboard'
    }
  },
  // /data-collector y /collector/dashboard eliminados en Lote 6.5 (2026-04-21)

  // 🎭 Multi-Role Dashboard Routes
  createLegacyAuthenticatedRedirect(
    '/profiles/analytics/executive',
    'LegacyExecutiveDashboardRedirect',
    '/super-admin',
    ['super_admin']
  ),
  createLegacyAuthenticatedRedirect(
    '/profiles/departmental',
    'LegacyDepartmentalDashboardRedirect',
    () => resolveCanonicalAliasRedirect(['group_manager', 'department_head'], '/group-manager'),
    ['group_manager', 'department_head']
  ),
  createLegacyAuthenticatedRedirect(
    '/profiles/technical',
    'LegacyTechnicalDashboardRedirect',
    () => resolveCanonicalAliasRedirect(['field_supervisor', 'route_operator', 'agency_employee'], '/field-supervisor'),
    ['field_supervisor', 'route_operator', 'agency_employee']
  ),

  // 📊 Analytics Routes
  {
    path: '/analytics',
    name: 'Analytics',
  component: () => import('@/views/analytics/DashboardAnalytics.vue'),
    meta: { 
      requiresAuth: true,
      // P13-V7B (refutation): `department_head` removed — its four named KPI cards all state "Sin datos";
      // no producer answers them for the role, and the department_head home does not link here.
      roles: ['super_admin'],
      title: 'Analytics',
      breadcrumb: 'Analytics',
      icon: 'mdi-chart-line',
      group: 'analytics'
    }
  },
  createLegacyAuthenticatedRedirect(
    '/profiles/analytics',
    'LegacyAnalyticsDashboardRedirect',
    '/super-admin',
    ['super_admin']
  ),
  {
    path: '/analytics/advanced',
    name: 'AdvancedAnalytics',
    component: () => import('@/views/SuperAdmin/AdvancedAnalyticsDashboard.vue'),
    meta: { 
      requiresAuth: true,
      roles: ['super_admin'],
      title: 'Analytics Avanzados',
      breadcrumb: 'Analytics Avanzados',
      icon: 'mdi-chart-timeline-variant',
      group: 'analytics'
    }
  },
  {
    path: '/business-intelligence',
    name: 'BusinessIntelligence',
    component: () => import('@/views/SuperAdmin/BusinessIntelligenceDashboard.vue'),
    meta: { 
      requiresAuth: true,
      roles: ['super_admin'],
      title: 'Business Intelligence',
      breadcrumb: 'Business Intelligence',
      icon: 'mdi-brain',
      group: 'analytics'
    }
  },

  // 📊 REPORTS SYSTEM - Sistema de Reportes Completo
  {
    path: '/reports',
    name: 'ReportsCenter',
    component: () => import('@/views/reports/EnterpriseReportsCenter.vue'),
    meta: { 
      requiresAuth: true,
  roles: ['group_manager', 'super_admin'],
      title: 'Centro de Reportes Empresarial',
      breadcrumb: 'Reportes',
      icon: 'mdi-chart-box-multiple',
      group: 'reports',
  // No exigimos permisos granulares para SuperAdmin/GroupManager
    }
  },
  {
    path: '/reports/executive',
    name: 'ExecutiveReports',
    component: () => import('@/components/reports/ExecutiveReports.vue'),
    meta: {
      requiresAuth: true,
  // P13-V5A-R1 (NEW-12): this route admitted `group_manager`, but its ONLY data producer
  // (`GET /api/administrations/:id/metrics`, reached through `stores/admin-metrics`) admits
  // super_admin / administration_manager / field_supervisor. A group_manager therefore reached a
  // page that could only ever answer 403 — a route advertising a capability its producer denies.
  // The route now admits exactly the roles the producer actually serves.
  roles: ['super_admin', 'administration_manager', 'field_supervisor'],
      title: 'Reportes Ejecutivos',
      breadcrumb: 'Reportes Ejecutivos',
      icon: 'mdi-briefcase-chart',
  group: 'reports'
    }
  },
  {
    path: '/reports/realtime',
    name: 'RealtimeReports',
    component: () => import('@/views/reports/RealtimeReports.vue'),
    meta: {
      requiresAuth: true,
  roles: ['super_admin', 'group_manager'],
      title: 'Reportes en Tiempo Real',
      breadcrumb: 'Reportes Tiempo Real',
      icon: 'mdi-speedometer',
  group: 'reports'
    }
  },
  {
    path: '/reports/analytics-suite',
    name: 'Phase6Suite',
    component: () => import('@/views/reports/Phase6Suite.vue'),
    meta: {
      requiresAuth: true,
      roles: ['super_admin', 'group_manager'],
  title: 'Suite Analítica',
  breadcrumb: 'Suite Analítica',
      icon: 'mdi-flash',
      group: 'reports'
    }
  },
  {
    path: '/reports/field-supervisor',
    name: 'FieldSupervisorReport',
    component: () => import('@/views/reports/FieldSupervisorReport.vue'),
    meta: { 
      requiresAuth: true,
      // P13-V7B (refutation): `department_head` removed — a field-supervisor report notice that labels
      // the department head "Supervisor"; nothing on it is the role's.
      roles: ['super_admin', 'group_manager', 'field_supervisor'],
      title: 'Reportes Ejecutivos',
      breadcrumb: 'Field Supervisor',
      icon: 'mdi-clipboard-check',
      group: 'reports',
      permissions: ['reports:field_supervisor']
    }
  },
  createLegacyAuthenticatedRedirect(
    '/reports/pos-supervisor',
    'LegacyFieldSupervisorReportRedirect',
    '/reports/field-supervisor',
    ['super_admin', 'group_manager', 'field_supervisor', 'department_head']
  ),
  {
    path: '/reports/group-manager',
    name: 'GroupManagerReport',
    component: () => import('@/views/reports/GroupManagerReport.vue'),
    meta: { 
      requiresAuth: true,
      roles: ['group_manager', 'super_admin'],
      title: 'Reporte Group Manager',
      breadcrumb: 'Group Manager',
      icon: 'mdi-account-group',
      group: 'reports',
      permissions: ['reports:group_manager']
    }
  },

  // 🏢 ENTERPRISE DASHBOARDS - Sistema de 7 Roles Empresariales
  createLegacyAuthenticatedRedirect(
    '/admin/dashboard',
    'LegacySuperAdminDashboardRedirect',
    '/super-admin',
    ['super_admin']
  ),
  createLegacyAuthenticatedRedirect(
    '/administrator/dashboard',
    'LegacyAdministratorDashboardRedirect',
    '/super-admin',
    ['super_admin']
  ),
  createLegacyAuthenticatedRedirect(
    '/manager/dashboard',
    'LegacyGroupManagerDashboardRedirect',
    '/group-manager',
    ['group_manager']
  ),
  createLegacyAuthenticatedRedirect(
    '/supervisor/dashboard',
    'LegacyFieldSupervisorDashboardRedirect',
    '/field-supervisor',
    ['field_supervisor']
  ),
  createLegacyAuthenticatedRedirect(
    '/operator/dashboard',
    'LegacyRouteOperatorDashboardRedirect',
    '/route-operator',
    ['route_operator']
  ),
  createLegacyAuthenticatedRedirect(
    '/agency/dashboard',
    'LegacyAgencyEmployeeDashboardRedirect',
    '/agency-employee',
    ['agency_employee']
  ),
  createLegacyAuthenticatedRedirect(
    '/analyst/dashboard',
    'LegacyDepartmentHeadDashboardRedirect',
    '/department-head',
    ['department_head']
  ),
  createLegacyAuthenticatedRedirect(
    '/jefe-departamento/dashboard',
    'LegacyDepartmentHeadDashboardRedirectEs',
    '/department-head',
    ['department_head']
  ),
  // /collector/dashboard eliminado en Lote 6.5 (2026-04-21)

  // 🚨 Incidents Management - ✅ ACCESO TOTAL ADMINISTRADOR
  //
  // P13-V7A-ADMINISTRATION-MANAGER-CAPABILITY-CLOSURE-WS1 · `administration_manager` leaves this route.
  // Every producer the page calls fails closed or refuses the role: the canonical incidence read policy
  // resolves administration_manager to NONE (V1D deferred the AM incidence read to V7A, and V7A does not
  // grant it — that would be a new business power, not a closure), `/api/users` refuses it, and the
  // "Nueva Incidencia" action posts to a writer whose policy also resolves it to NONE. A route that can
  // only ever render an empty table and a refused action is a promise, not a capability; the sidebar
  // mirror (`hasIncidentsAccess`) is narrowed with it. Recorded as `AM_INCIDENCE_READ = NOT_CLAIMED`.
  {
    path: '/incidents',
    name: 'Incidents',
    component: () => import('@/views/incidents/AdvancedIncidentsManagement.vue'),
    meta: {
      requiresAuth: true,
      roles: ['super_admin', 'department_head'],
      title: 'Gestión de Incidencias',
      breadcrumb: 'Incidencias',
      icon: 'mdi-alert-circle',
      group: 'operations',
      badge: '12',
      badgeColor: 'error',
      adminFullAccess: true // ✅ Sin restricciones para admin
    }
  },

  // 📦 Inventory Management — P13-V2B: los metas NO LEÍDOS por ningún guard (adminFullAccess ·
  // visibilityCheck) y el badge fabricado ('4' hardcodeado) fueron retirados de las rutas de
  // inventario. meta.roles ESPEJA la matriz §R5 que el servidor aplica; el servidor re-decide
  // cada lectura/mutación.
  {
    path: '/inventory',
    name: 'Inventory',
    component: () => import('@/views/inventory/InventoryManagement.vue'),
    meta: {
      requiresAuth: true,
      roles: ['super_admin', 'administration_manager'],
      title: 'Gestión de Inventario',
      breadcrumb: 'Inventario',
      icon: 'mdi-package-variant',
      group: 'operations'
    }
  },

  // � Inventory by Administration (vista reutilizada con scope por administración)
  {
    path: '/inventory/administrations/:id',
    name: 'InventoryAdministration',
    component: () => import('@/views/inventory/InventoryManagement.vue'),
    meta: {
      requiresAuth: true,
      roles: ['super_admin', 'administration_manager'],
      title: 'Inventario por Administración',
      breadcrumb: [
        { name: 'Inventario', to: '/inventory' },
        { name: 'Administración' }
      ],
      icon: 'mdi-domain',
      group: 'operations'
    }
  },

  // �📋 Inventory Requests & Inbox - Solicitudes y Aprobaciones
  {
    path: '/inventory/requests',
    name: 'InventoryRequests',
    component: () => import('@/views/inventory/InventoryRequests.vue'),
    meta: {
      requiresAuth: true,
      roles: ['super_admin', 'administration_manager'],
      title: 'Solicitudes de Inventario',
      breadcrumb: [
        { name: 'Inventario', to: '/inventory' },
        { name: 'Solicitudes' }
      ],
      icon: 'mdi-clipboard-list',
      group: 'operations'
    }
  },

  // 🚚 Inventory outbound orders tracker
  {
    path: '/inventory/outbound',
    name: 'OutboundOrders',
    component: () => import('@/views/inventory/OutboundOrders.vue'),
    meta: {
      requiresAuth: true,
      roles: ['super_admin', 'administration_manager'],
      title: 'Órdenes de salida',
      breadcrumb: [
        { name: 'Inventario', to: '/inventory' },
        { name: 'Órdenes de salida' }
      ],
      icon: 'mdi-truck-delivery',
      group: 'operations'
    }
  },

  // 🏪 P13-V2B (INV-05, leg FE · PD-V2-06): jornada de solicitud del agency_employee — pantalla
  // mínima acotada sobre el patrón del panel de campo. El servidor deriva TODO el contexto
  // (agencia/administración/grupo) del punto asignado PERSISTIDO del actor; la pantalla sólo
  // captura ítem + cantidad entera + prioridad + justificación, y el seguimiento llega por la
  // notificación durable K4B (el rol no tiene lector de solicitudes admitido por el servidor).
  //
  // WS4 · H3 (MGI-PILOT-W2-DEFERRED-CLIENT-CAPABILITY-PERIMETER-CONVERGENCE-WS4 · DEFERRED-API-INVENTORY):
  // `agency_employee` removed. The backend contains the two surfaces this page consumes
  // (`POST /inventory/requests`, `PUT /inventory/requests/:id/cancel`) from the delivered pilot identities —
  // V2 Inventory is DEFERRED_NOT_CLOSED in the signed contract, and a completed vertical is not a sold one —
  // so admitting the client here would mount a form whose every submit answers 403. The route and its
  // component are PRESERVED for their future owner (the same shape W2 used for the field-supervisor pages);
  // only client admission is removed, and the drawer entry mirrors this record (SidebarNavigation).
  {
    path: '/agency-employee/inventory-request',
    name: 'AgencyEmployeeInventoryRequest',
    component: () => import('@/views/agency-employee/AgencyEmployeeInventoryRequestView.vue'),
    meta: {
      requiresAuth: true,
      roles: ['super_admin'],
      title: 'Solicitar Suministros',
      breadcrumb: [
        { name: 'Agencia', to: '/agency-employee' },
        { name: 'Solicitar Suministros' }
      ],
      icon: 'mdi-package-variant-plus',
      group: 'operations'
    }
  },

  // P13-V2A C7 (PD-V2-01, retiro simultáneo): las rutas de Workspaces de Inventario
  // (/inventory/workspaces, /inventory/workspaces/analytics, /inventory/workspaces/:id) fueron
  // RETIRADAS — el backend workspace es un 410 estable (superficie operacional retirada; los
  // datos históricos se preservan en solo-lectura). Las vistas se eliminan en V2B.

  // 📅 ENTERPRISE AGENDA (P12 §6) — shell anidado; cada hijo declara SUS
  // PROPIOS meta.roles (sin huecos de herencia). La ruta es la única fuente
  // de la vista activa.
  {
    path: '/agenda',
    component: () => import('@/views/Agenda/AgendaShell.vue'),
    meta: {
      requiresAuth: true,
      roles: [...AGENDA_PILOT_SURFACE_ROLES],
      group: 'operations'
    },
    children: [
      {
        path: '',
        redirect: { name: 'agenda-my' }
      },
      {
        path: 'my',
        name: 'agenda-my',
        component: () => import('@/views/Agenda/MyAgendaView.vue'),
        meta: {
          requiresAuth: true,
          roles: [...AGENDA_PILOT_SURFACE_ROLES],
          agendaView: 'my',
          title: 'Mi Agenda',
          breadcrumb: [{ name: 'Agenda' }, { name: 'Mi Agenda' }],
          icon: 'mdi-calendar-account',
          group: 'operations'
        }
      },
      {
        path: 'assigned',
        name: 'agenda-assigned',
        component: () => import('@/views/Agenda/AssignedToMeView.vue'),
        meta: {
          requiresAuth: true,
          roles: [...AGENDA_PILOT_SURFACE_ROLES],
          agendaView: 'assigned',
          title: 'Asignadas a mí',
          breadcrumb: [{ name: 'Agenda' }, { name: 'Asignadas a mí' }],
          icon: 'mdi-clipboard-account',
          group: 'operations'
        }
      },
      {
        path: 'organized',
        name: 'agenda-organized',
        component: () => import('@/views/Agenda/OrganizedByMeView.vue'),
        meta: {
          requiresAuth: true,
          roles: [...AGENDA_PILOT_SURFACE_ROLES],
          agendaView: 'organized',
          title: 'Organizadas por mí',
          breadcrumb: [{ name: 'Agenda' }, { name: 'Organizadas por mí' }],
          icon: 'mdi-account-tie',
          group: 'operations'
        }
      },
      {
        path: 'team',
        name: 'agenda-team',
        component: () => import('@/views/Agenda/TeamUnitView.vue'),
        meta: {
          requiresAuth: true,
          roles: [...AGENDA_PILOT_SURFACE_ROLES],
          agendaView: 'team',
          title: 'Agenda de Equipo',
          breadcrumb: [{ name: 'Agenda' }, { name: 'Equipo' }],
          icon: 'mdi-account-group',
          group: 'operations'
        }
      },
      {
        path: 'delegated',
        name: 'agenda-delegated',
        component: () => import('@/views/Agenda/DelegatedAgendasView.vue'),
        meta: {
          requiresAuth: true,
          roles: [...AGENDA_PILOT_SURFACE_ROLES],
          agendaView: 'delegated',
          title: 'Agendas Delegadas',
          breadcrumb: [{ name: 'Agenda' }, { name: 'Delegadas' }],
          icon: 'mdi-account-switch',
          group: 'operations'
        }
      },
      {
        path: 'invitations',
        name: 'agenda-invitations',
        component: () => import('@/views/Agenda/InvitationsView.vue'),
        meta: {
          requiresAuth: true,
          roles: [...AGENDA_PILOT_SURFACE_ROLES],
          agendaView: 'invitations',
          title: 'Invitaciones',
          breadcrumb: [{ name: 'Agenda' }, { name: 'Invitaciones' }],
          icon: 'mdi-email-outline',
          group: 'operations'
        }
      },
      {
        path: 'items/:itemId',
        name: 'agenda-item',
        component: () => import('@/views/Agenda/AgendaItemDetailRoute.vue'),
        meta: {
          requiresAuth: true,
          roles: [...AGENDA_PILOT_SURFACE_ROLES],
          agendaView: 'item',
          title: 'Detalle de Ítem de Agenda',
          breadcrumb: [{ name: 'Agenda' }, { name: 'Detalle' }],
          icon: 'mdi-calendar-search',
          group: 'operations'
        }
      }
    ]
  },
  // Concepto legacy 'responsabilidad' retirado: redirección por rol
  // (patrón resolveCanonicalAliasRedirect — coordinación → team, resto → my).
  {
    path: '/agenda/responsibility',
    name: 'agenda-responsibility',
    redirect: () => {
      const authStore = useAuthStore()
      const currentRole = authStore.userRole || ''
      return AGENDA_COORDINATION_ROLES.includes(currentRole)
        ? '/agenda/team'
        : '/agenda/my'
    },
    meta: {
      requiresAuth: true,
      roles: [...AGENDA_PILOT_SURFACE_ROLES],
      title: 'Agenda - Responsabilidad (legacy)',
      group: 'legacy-redirects',
      legacyRedirect: true
    }
  },

  // P13-V8 (DEC-V8-01): the /automations surface is RETIRED — an unmatched URL is /not-found (V7B guard).

  // �👥 User Management - ✅ ACCESO TOTAL ADMINISTRADOR
  {
    path: '/users',
    name: 'UserManagement',
    component: () => import('@/views/users/UserManagement.vue'),
    meta: { 
      requiresAuth: true,
      // P13-V7B: `group_manager` removed. The page is a LIST, and its reader (`GET /api/users`) is
      // vendor-only; its helper reads answer the role 501. A group manager therefore saw an empty
      // table above row actions it could never reach. The role's per-user writers stay where they
      // are in the backend; this page is simply not a surface that can serve it.
      roles: ['super_admin'],
      title: 'Gestión de Usuarios',
      breadcrumb: [{ name: 'Usuarios' }],
      icon: 'mdi-account-group',
      group: 'administration',
      adminFullAccess: true // ✅ Sin restricciones para admin
    }
  },

  // 🏢 Department Management - ✅ PASO 5.8.2 COMPLETADO
  {
    path: '/management/departments',
    name: 'DepartmentManagement',
    component: () => import('@/views/management/DepartmentManagement.vue'),
    meta: { 
      requiresAuth: true,
      // P13-V7B (refutation): `department_head` removed. Every request this page makes refuses the role
      // (GET/POST /api/departments admit super_admin and group_manager; PUT adds administration_manager),
      // yet it offered "Nuevo Departamento". The department_head home links to neither management page.
      roles: ['super_admin', 'group_manager'],
      title: 'Gestión de Departamentos',
      breadcrumb: [
        { name: 'Gestión', path: '/management' },
        { name: 'Departamentos' }
      ],
      icon: 'mdi-office-building',
      group: 'administration',
      description: 'CRUD completo para gestión departamental con jerarquía',
      adminFullAccess: true
    }
  },

  // 👥 User-Department Management
  {
    path: '/management/users-departments',
    name: 'UserDepartmentManagement', 
    component: () => import('@/views/management/UserDepartmentManagement.vue'),
    meta: { 
      requiresAuth: true,
      // P13-V7B (refutation): `department_head` removed — its department list and membership readers
      // (GET /api/departments, /api/memberships) refuse the role; the page could only show refusals.
      roles: ['super_admin', 'group_manager'],
      title: 'Gestión Usuario-Departamentos',
      breadcrumb: [
        { name: 'Gestión', path: '/management' },
        { name: 'Usuario-Departamentos' }
      ],
      icon: 'mdi-account-group-outline',
      group: 'administration',
      description: 'Asignación y gestión de personal departamental',
      adminFullAccess: true
    }
  },

  // 🗺️ Maps & Geolocation - ✅ ACCESO TOTAL ADMINISTRADOR + DEPARTMENT HEAD
  //
  // P13-V7A-ADMINISTRATION-MANAGER-CAPABILITY-CLOSURE-WS1 · `administration_manager` leaves the three map
  // routes. Every layer the map composes is refused or fails closed for the role (`/api/incidences` →
  // NONE, `/api/users` and `/api/routes` → 403): the page could only ever render an empty map. The V4
  // route/GPS domain is not touched — this is the role's advertisement made truthful, mirrored in the
  // sidebar (`hasMapsAccess`).
  {
    path: '/maps',
    name: 'Maps',
    component: () => import('@/views/maps/MapsView.vue'),
    meta: {
      requiresAuth: true,
      roles: ['super_admin'],
      title: 'Mapas Interactivos MGI',
      breadcrumb: [{ name: 'Mapas', path: '/maps' }],
      icon: 'mdi-map',
      group: 'operations',
      description: 'Sistema de visualización geoespacial de incidencias y supervisores',
      adminFullAccess: true // ✅ Sin restricciones para admin
    }
  },
  {
    path: '/maps/interactive',
    name: 'InteractiveMap',
    component: () => import('@/views/maps/InteractiveMap.vue'),
    meta: {
      requiresAuth: true,
      roles: ['super_admin'],
      title: 'Mapa Interactivo',
      breadcrumb: [
        { name: 'Mapas', path: '/maps' },
        { name: 'Mapa Interactivo' }
      ],
      icon: 'mdi-map-marker-multiple',
      group: 'operations',
      parent: 'Maps',
      description: 'Vista avanzada del mapa con controles interactivos',
      adminFullAccess: true // ✅ Sin restricciones para admin
    }
  },
  {
    path: '/maps/monitoring',
    name: 'MapMonitoring',
    component: () => import('@/views/maps/MapsView.vue'),
    meta: {
      requiresAuth: true,
      roles: ['super_admin'],
      title: 'Monitoreo en Tiempo Real',
      breadcrumb: [
        { name: 'Mapas', path: '/maps' },
        { name: 'Monitoreo' }
      ],
      icon: 'mdi-monitor-dashboard',
      group: 'operations',
      parent: 'Maps',
      description: 'Monitoreo en tiempo real de incidencias y supervisores'
    }
  },
  {
    path: '/profiles',
    name: 'Profiles',
    component: () => import('@/views/ProfilesView.vue'),
    meta: {
      requiresAuth: true,
      // P13-V7A: administration_manager removed — the view is a "será implementada próximamente"
      // placeholder with no producer; an unbacked promise is not advertised to the role.
      roles: ['super_admin'],
      title: 'Perfiles',
      breadcrumb: 'Perfiles',
      icon: 'mdi-card-account-details',
      group: 'management'
    }
  },

  // 🏢 Department Management
  // W2 DH-DEP: `department_head` retirado de esta superficie. La vista amplia de estructura
  // departamental no la vende el piloto firmado para ese rol: el servicio de navegación no tiene
  // bloque DEPARTMENT_HEAD y la capacidad que respalda la página (`GET /api/departments`) está
  // clasificada BLOCKS_W2 en el ledger del padre. Hoy la página ni siquiera la alcanza —compone
  // `/api/api/departments` y recibe 404—, así que retirar la admisión ahora impide que un jefe de
  // departamento empiece a alcanzar una ruta no contratada el día que otro dueño repare esa
  // composición. `super_admin` conserva la superficie administrativa: esto es contención por rol,
  // no retiro de la capacidad.
  {
    path: '/departments',
    name: 'Departments',
    component: () => import('@/views/DepartmentHead/DepartmentStructureView.vue'),
    meta: {
      requiresAuth: true,
      roles: ['super_admin'],
      title: 'Gestión de Departamentos',
      breadcrumb: 'Departamentos',
      icon: 'mdi-office-building',
      group: 'management'
    }
  },
  // MGI-PILOT-W2-V3-ASSIGNMENTS-PILOT-PERIMETER-R1 · V3 assignment management is DEFERRED and is not
  // sold in the first pilot. Both records below are ONE capability admitted through ONE mechanism, and
  // the only assignment-family producer these surfaces use is `/api/task-assignments` — a family whose
  // backend admits only super_admin / administration_manager / group_manager
  // (taskAssignmentRoutes.js:22-23). (AssignmentSystemView additionally performs non-assignment
  // support reads such as /auth/validate; those are not an assignment authority.) A
  // department_head was therefore offered "Nueva", "Editar", "Auto-asignar", "Auto-delegar",
  // "Delegar" and "Eliminar" against an API that answers 403 for that role: a capability advertised by
  // the surface and refused by its own producer.
  //
  // Contained BY ROLE through the route authority that already exists. `super_admin` keeps both, so
  // this is containment and not retirement; no component is deleted, no V3 is implemented, no flag is
  // introduced and no service or API authority is touched. The sibling records that mount the same
  // AssignmentsManagement component for `/administration-manager/*` are a different owner (V7A) and
  // are deliberately left exactly as they were — they never admitted department_head.
  {
    path: '/assignments',
    name: 'Assignments',
    component: () => import('@/views/DepartmentHead/AssignmentSystemView.vue'),
    meta: {
      requiresAuth: true,
      roles: ['super_admin'],
      title: 'Sistema de Asignaciones',
      breadcrumb: 'Asignaciones',
      icon: 'mdi-clipboard-text',
      group: 'operations'
    }
  },
  {
    path: '/assignments/management',
    name: 'AssignmentsManagement',
    component: () => import('@/views/assignments/AssignmentsManagement.vue'),
    meta: {
      requiresAuth: true,
      roles: ['super_admin'],
      title: 'Gestión de Asignaciones',
      breadcrumb: 'Gestión de Asignaciones',
      icon: 'mdi-clipboard-multiple',
      group: 'operations'
    }
  },

  // 🗺️ Route Management - SuperAdmin (incluye tabs de rutas y calendario de asignaciones)
  {
    path: '/routes/management',
    name: 'RouteManagement',
    component: () => import('@/views/routes/RouteManagement.vue'),
    meta: {
      requiresAuth: true,
      // P13-V7B: `administration_manager` and `group_manager` removed. Every reader and writer this
      // console issues refuses an administration manager, and a group manager is refused the route
      // list, the operators, the route incidents and every write. Route planning and execution are
      // V4 capability: the console is CONTAINED to the vendor here, not repaired and not rebuilt.
      roles: ['super_admin'],
      title: 'Gestión de Rutas',
      breadcrumb: 'Gestión de Rutas',
      icon: 'mdi-map-marker-path',
      group: 'operations'
    }
  },

  //  System Testing & Configuration
  createContainedLabRoute({
    path: '/test-vue',
    name: 'TestVue',
    component: () => import('@/views/TestVueView.vue'),
    meta: { 
      requiresAuth: false,
      title: 'Test Vue',
      breadcrumb: 'Test Vue',
      group: 'testing'
    }
  }),
  createContainedLabRoute({
    path: '/test-components',
    name: 'TestComponents',
    component: () => import('@/views/TestComponentsView.vue'),
    meta: { 
      requiresAuth: false,
      title: 'Test Components',
      breadcrumb: 'Test Components',
      group: 'testing'
    }
  }),
  createContainedLabRoute({
    path: '/role-demo',
    name: 'RoleDemo',
    component: () => import('@/views/RoleDemoView.vue'),
    meta: { 
      requiresAuth: false,
      title: 'Demo Componentes por Rol',
      breadcrumb: 'Role Demo',
      group: 'testing'
    }
  }),

  // 🚫 Error Pages
  {
    path: '/access-denied',
    name: 'AccessDenied',
    component: () => import('@/views/errors/AccessDenied.vue'),
    meta: { 
      title: 'Acceso Denegado',
      breadcrumb: 'Error 403',
      layout: 'default'
    }
  },
  {
    path: '/not-found',
    name: 'NotFound',
    component: () => import('@/views/errors/NotFound.vue'),
    meta: { 
      title: 'Página no encontrada',
      breadcrumb: 'Error 404',
      layout: 'default'
    }
  },
  {
    path: '/access-denied',
    name: 'AccessDenied',
    component: () => import('@/views/errors/AccessDenied.vue'),
    meta: { 
      requiresAuth: false,
      title: 'Acceso Denegado',
      breadcrumb: 'Error 403',
      layout: 'default'
    }
  },
  {
    path: '/error',
    name: 'Error',
    component: () => import('@/views/errors/AccessDenied.vue'),
    meta: { 
      requiresAuth: false,
      title: 'Error del Sistema',
      breadcrumb: 'Error',
      layout: 'default'
    }
  },

  // 📊 Dashboard Module Routes - PASO 5.12.2
  ...dashboardRoutes,
]

// 🚀 Router Creation with Enhanced History Mode
const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  // ⚡ Scroll behavior for smooth navigation
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }
    // Smooth scroll to top for new pages
    if (to.hash) {
      return {
        el: to.hash,
        behavior: 'smooth'
      }
    }
    return { top: 0, behavior: 'smooth' }
  }
})

// ─────────────────────────────────────────────────────────────
// PR-11: GUARD ÚNICO — exactamente 1 beforeEach, 1 afterEach
// ─────────────────────────────────────────────────────────────

router.beforeEach(async (to, from, next) => {
  const navigationStore = useNavigationStore()

  // 1. Tracking + loading
  navigationStore.setLoading(true)
  navigationStore.trackPageView(to.path, String(to.name))

  try {
    // 2. Guard canónico (auth + roles)
    const redirect = await canonicalGuard(to, from)
    if (redirect) {
      navigationStore.setLoading(false)
      return next(redirect)
    }

    // 3. Breadcrumbs
    if (to.meta.breadcrumb) {
      navigationStore.updateBreadcrumbs(to)
    }

    // 4. Prefetch ligero (no bloqueante)
    try {
      const { prefetchForRoute } = await import('./prefetch')
      await prefetchForRoute(String(to.name))
    } catch (_e) {
      // prefetch optional — no bloquear navegación
    }

    // 5. Permitir navegación
    next()
  } catch (error) {
    console.error('[Router] beforeEach error:', error)
    navigationStore.setLoading(false)
    next('/access-denied')
  }
})

router.afterEach((to, from, failure) => {
  const navigationStore = useNavigationStore()

  setTimeout(() => navigationStore.setLoading(false), 200)

  if (!failure) {
    navigationStore.addToHistory({
      path: to.path,
      name: String(to.name),
      timestamp: Date.now(),
      title: to.meta.title || String(to.name)
    })

    if (to.meta.title) {
      document.title = `${to.meta.title} - MGI Sistema`
    }
  }
})

router.onError((error, to, from) => {
  console.error('[Router] Error:', error, { to: to.path, from: from.path })
})

export default router
